import React from 'react';
import { useClipboard } from '@mantine/hooks';
import {
  getErrorMessage,
  logger,
  showErrorToast,
  showSuccessToast,
  showToast,
} from 'src/utils';

/**
 * @registry Drives the "copy external link / revoke shared links" cluster shared by workflow forms (Changes, Transmittals). Copies an attachment's tokenized externalUrl to the clipboard with a toast, and runs a confirm-modal revoke that rotates the entity's external access token via the caller-supplied `rotate` call, then hands the refreshed DTO to `onRevoked` so the form can re-baseline. Pair with RevokeLinksModal for the confirmation dialog.
 */
export function useRevokeExternalLinks<TResult>({
  rotate,
  onRevoked,
  logLabel,
}: {
  /** Rotates the entity's external access token and returns the refreshed DTO. */
  rotate: () => Promise<TResult>;
  /** Invoked with the refreshed DTO after a successful revoke so the caller can re-baseline form state. */
  onRevoked: (result: TResult) => void;
  /** Message logged when the rotate call fails. */
  logLabel: string;
}) {
  const clipboard = useClipboard({ timeout: 1500 });
  const [modalOpen, setModalOpen] = React.useState(false);
  const [revoking, setRevoking] = React.useState(false);

  const copyLink = (file: { externalUrl?: string }) => {
    if (!file.externalUrl) return;
    clipboard.copy(file.externalUrl);
    showToast('Link copied — anyone with it can view until you revoke.');
  };

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const revoke = async () => {
    setRevoking(true);
    try {
      const result = await rotate();
      setModalOpen(false);
      showSuccessToast('Shared links revoked.');
      onRevoked(result);
    } catch (err: unknown) {
      logger.error(logLabel, { err });
      showErrorToast(getErrorMessage(err));
    }
    setRevoking(false);
  };

  return { copyLink, modalOpen, openModal, closeModal, revoking, revoke };
}
